import { BrandLogo } from '@brightdrop/bd-ui';
import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { cx } from '@emotion/css';
import { Box, Divider, Drawer, IconButton, List } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import CloseDrawerIcon from '~/assets/icons/closeDrawerIcon.svg?react';
import OrganizationIcon from '~/assets/icons/leftMenu/organizationIcon.svg?react';
import GMLogo from '~/assets/images/gm-logo.svg?react';
import { BackToHomeLink } from '~/common/components/BackToHomeLink/BackToHomeLink';
import { ONSTAR_FLEET_INTELLIGENCE_REBRAND_FLAG } from '~/common/models/featureFlags.model';
import { SUPPORT_CONTACT_PATH } from '~/common/models/pages/supportPages.model';
import { GMF_ORGANIZATIONS_PATH } from '~/common/utils/route/gmfAdvisorPolicy';
import { getProfileNavBarItems } from '~/features/navigationDrawer/config/account/profile.navBarItems.config';
import { getSettingsNavBarItems } from '~/features/navigationDrawer/config/account/setting.navBarItems.config';
import { getSupportNavBarItems } from '~/features/navigationDrawer/config/account/support.navBarItems.config';
import {
  getAppNameFromPath,
  isAccountAreaAppName,
} from '~/features/navigationDrawer/config/navDrawer.config';
import { DrawerHeader } from '~/features/navigationDrawer/GlobalNavDrawer';
import useStyles from '~/features/navigationDrawer/GlobalNavDrawer.styles';
import NavDrawerItem from '~/features/navigationDrawer/NavDrawerItem';

/** Keeps Organizations and supported self-account navigation within the GMF subset. */
const GmfAdvisorNav = ({
  expanded,
  onToggle,
  resolved,
}: {
  expanded: boolean;
  onToggle: () => void;
  resolved: boolean;
}) => {
  const { t } = useTranslation(['organization', 'common']);
  const { classes } = useStyles({ isMinimal: true });
  const { getFlag } = useFeatureFlags();
  const { pathname } = useLocation();
  const isAccountArea = isAccountAreaAppName(getAppNameFromPath(pathname));
  const rebranded = Boolean(getFlag(ONSTAR_FLEET_INTELLIGENCE_REBRAND_FLAG));
  const label = t('organization:organization_plural');
  const drawerClass = expanded ? classes.drawerOpen : classes.drawerClose;
  const accountItems = [
    ...getProfileNavBarItems(),
    ...getSettingsNavBarItems(),
    ...getSupportNavBarItems(getFlag).map((item) => ({
      ...item,
      children: expanded
        ? item.children.filter((child) => child.path !== SUPPORT_CONTACT_PATH)
        : undefined,
    })),
  ];
  return (
    <Drawer
      variant="permanent"
      className={cx(classes.drawer, drawerClass)}
      classes={{ paper: drawerClass }}
    >
      <DrawerHeader className={classes.logoContainer}>
        <Link to={GMF_ORGANIZATIONS_PATH}>
          <Box
            className={cx('appLogo', { isOpen: expanded })}
            data-testid="app-logo"
          >
            {!expanded && !rebranded ? (
              <GMLogo
                width={32}
                height={32}
                role="img"
                aria-label={t('common:navDrawer.gmFleet')}
              />
            ) : (
              <BrandLogo
                width={expanded ? (rebranded ? 231 : 106) : 32}
                height={32}
                isNavCollapsed={!expanded}
              />
            )}
          </Box>
        </Link>
      </DrawerHeader>
      {resolved && isAccountArea && (
        <Box className={cx(classes.backToLinkContainer, { isOpen: expanded })}>
          <BackToHomeLink to={GMF_ORGANIZATIONS_PATH} />
        </Box>
      )}
      <Box
        component="nav"
        aria-label={isAccountArea ? String(t('common:account')) : label}
        className={classes.drawerBody}
      >
        <List className={classes.navDrawerList}>
          {resolved &&
            isAccountArea &&
            accountItems.map((item) => (
              <NavDrawerItem
                key={item.testId}
                {...item}
                icon={
                  expanded ? (
                    item.icon
                  ) : (
                    <span
                      role="img"
                      aria-label={
                        typeof item.label === 'string' ? item.label : undefined
                      }
                    >
                      {item.icon}
                    </span>
                  )
                }
                isNavDrawerOpen={expanded}
              />
            ))}
          {resolved && !isAccountArea && (
            <NavDrawerItem
              label={label}
              path={GMF_ORGANIZATIONS_PATH}
              testId="org-list"
              icon={
                <OrganizationIcon
                  role="img"
                  aria-label={!expanded ? label : undefined}
                />
              }
              isNavDrawerOpen={expanded}
            />
          )}
        </List>
        <Box component="footer" className={classes.drawerFooterStandalone}>
          <Divider className={classes.divider} />
          <Box className={classes.drawerCollapseOnlyWrap}>
            <IconButton
              onClick={onToggle}
              disabled={!resolved}
              aria-expanded={expanded}
              aria-label={t(
                `organization:restrictedAdvisor.${expanded ? 'collapse' : 'expand'}`
              )}
              sx={!expanded ? { transform: 'rotate(180deg)' } : undefined}
            >
              <CloseDrawerIcon aria-hidden />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default GmfAdvisorNav;
